/**
 * ModerationStatsSection Component
 *
 * Moderation Stats Panels Grid — can be used in header (V1) or page body (V2).
 * Extracted from CommunityModerationHeaderSection per one-component-per-file rule.
 */

import { FEATURE_FLAGS } from '@core/domain';
import { useIsFeatureEnabled } from '@core/hooks';
import {
  ModerationTotalCasesPanel,
  ModerationCasesOpenPanel,
  ModerationActiveModeratorsPanel,
  ModerationCasesCompletedPanel,
} from '@moderation/components/ModerationStatsPanel';
import { Dropdown } from '@soteria/design-system-ve';

import type { ModerationStats, TimeRange } from './CommunityModerationHeaderSection';
import { TIME_RANGE_OPTIONS } from './CommunityModerationHeaderSection';
import * as styles from './CommunityModerationHeaderSection.css';

export interface ModerationStatsPanelsProps {
  stats: ModerationStats;
  isLoading?: boolean;
  selectedTimeRange: TimeRange;
  onTimeRangeChange: (value: string) => void;
}

export function ModerationStatsSection({ stats, isLoading = false, selectedTimeRange, onTimeRangeChange }: ModerationStatsPanelsProps) {
  const isStatsCardsVisible = useIsFeatureEnabled(FEATURE_FLAGS.SHOW_MODERATION_STATS_CARDS);

  if (!isStatsCardsVisible) return null;

  return (
    <div className={styles.statsSection}>
      {/* Time Range Filter */}
      <div className={styles.timeRangeContainer}>
        <label className={styles.timeRangeLabel} htmlFor="timeRangeDropdown">
          Showing data for:
        </label>
        <Dropdown
          className={styles.timeRangeDropdown}
          id="timeRangeDropdown"
          options={[...TIME_RANGE_OPTIONS]}
          selectedItem={selectedTimeRange}
          size="medium"
          onSelectionChange={onTimeRangeChange}
        />
      </div>

      {/* 4-Panel Stats Grid */}
      <div className={styles.fourPanelContainer}>
        <div className={styles.fourPanelGrid}>
          <ModerationTotalCasesPanel isLoading={isLoading} totalCases={stats.totalCases} />
          <ModerationCasesOpenPanel casesOpen={stats.casesOpen} isLoading={isLoading} />
          <ModerationActiveModeratorsPanel activeModerators={stats.activeModerators} isLoading={isLoading} />
          <ModerationCasesCompletedPanel casesCompleted={stats.casesCompleted} isLoading={isLoading} />
        </div>
      </div>
    </div>
  );
}
